import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/Logo';
import NewsTicker from '../components/NewsTicker';
import PushOptInBanner from '../components/PushOptInBanner';
import ContractUpdateBanner from '../components/ContractUpdateBanner';
import BlogSlider from '../components/BlogSlider';
import IcalCalendarSection from '../components/IcalCalendarSection';
import DeadlinesCalendar from '../components/DeadlinesCalendar';
import OnboardingCallBanner from '../components/OnboardingCallBanner';
import HostLandPromoBanner from '../components/HostLandPromoBanner';
import RevenueTrendChart from '../components/RevenueTrendChart';
import { Button } from '../components/ui/button';
import { translations, languageFlags, languageNames } from '../lib/translations';
import { 
  FileText, 
  Plus, 
  Download, 
  Link as LinkIcon, 
  Trash, 
  SignOut,
  Bell,
  Eye,
  FilePdf,
  Notebook,
  House,
  CreditCard,
  Clock,
  Star,
  UserCircle,
  ShareNetwork,
  Briefcase,
  DeviceMobile,
  CaretDown,
  Warning,
  ChartBar,
  Crown,
  EnvelopeSimple,
  CurrencyEur,
  TrendUp,
  CalendarBlank,
  Receipt,
  CheckCircle,
  CloudArrowDown,
  Calculator,
  MapPin,
  ArrowRight,
  ShieldCheck,
  Calendar,
  Buildings,
  IdentificationCard,
  Sparkle as SparkleIcon,
  QrCode as QrCodeIcon,
  Coins as CoinsIcon,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Build an italian date-relative string like "tra 28 giorni" / "domani" / "oggi"
function relDay(days) {
  if (days == null) return '';
  if (days === 0) return 'oggi';
  if (days === 1) return 'domani';
  if (days < 0) return `${-days}gg fa`;
  return `tra ${days} giorn${days === 1 ? 'o' : 'i'}`;
}

// Map event type -> label + emoji
const EVENT_LABELS = {
  concerto: { label: 'concerto', emoji: '🎤' },
  fiera: { label: 'fiera', emoji: '🏢' },
  sportivo: { label: 'evento sportivo', emoji: '🏆' },
  festival: { label: 'festival', emoji: '🎉' },
};

// Returns an array of {key, icon, label, color} coach insights based on backend data
function buildCoachInsights(coach) {
  if (!coach) return [];
  const out = [];

  // Yesterday income
  if (coach.yesterday?.gross > 0) {
    out.push({
      key: 'yesterday',
      emoji: '💰',
      text: `Ieri hai incassato €${coach.yesterday.gross.toFixed(0)}`,
      color: 'text-emerald-600',
    });
  }

  // Pending contracts
  if (coach.pending_contracts > 0) {
    out.push({
      key: 'pending',
      emoji: '✍️',
      text: `${coach.pending_contracts} contratt${coach.pending_contracts === 1 ? 'o' : 'i'} in attesa di firma`,
      color: 'text-amber-600',
    });
  }

  // Missing schedine — disabled: modulo Schedine Alloggiati temporaneamente
  // rimosso dalla dashboard finché non è pronto.
  // if (coach.missing_schedine > 0) { ... }

  // Next event
  if (coach.next_event) {
    const lbl = EVENT_LABELS[coach.next_event.type] || { label: 'evento', emoji: '📅' };
    const rel = relDay(coach.next_event.days_to);
    out.push({
      key: 'event',
      emoji: lbl.emoji,
      text: `${lbl.label.charAt(0).toUpperCase() + lbl.label.slice(1)} a ${coach.next_event.city} ${rel}`,
      color: 'text-rose-600',
    });
  }

  return out;
}

// ==================== YEAR-TO-DATE PROFIT STRIP (payout-centric) ====================
// 3 compact cards (Incassi · Spese · Profitto) for the current year, sourced from
// `/api/accounting/summary` (the new payout-centric endpoint). Each card is
// clickable and routes the user to /contabilita for the full breakdown.
function ProfitYearStrip({ accSummary, onNavigate }) {
  const year = new Date().getFullYear();
  const byMonth = accSummary?.by_month || {};

  // Sum payout net + expenses for entries belonging to current year only.
  let incassi = 0;
  let spese = 0;
  Object.entries(byMonth).forEach(([month, vals]) => {
    if (!month.startsWith(String(year))) return;
    incassi += vals.net || 0;
    spese += vals.expenses || 0;
  });
  const profitto = incassi - spese;
  const hasData = (accSummary?.payouts_count || 0) > 0 || incassi > 0 || spese > 0;

  const cards = [
    {
      key: 'incassi',
      label: `Incassi ${year}`,
      value: incassi,
      icon: TrendUp,
      tone: { bar: 'bg-emerald-500', text: 'text-emerald-700', ring: 'group-hover:ring-emerald-500/20' },
      hint: 'Bonifici ricevuti (netti)',
    },
    {
      key: 'spese',
      label: `Spese ${year}`,
      value: spese,
      icon: Receipt,
      tone: { bar: 'bg-rose-500', text: 'text-rose-700', ring: 'group-hover:ring-rose-500/20' },
      hint: 'Costi registrati',
    },
    {
      key: 'profitto',
      label: `Profitto ${year}`,
      value: profitto,
      icon: CurrencyEur,
      tone: profitto >= 0
        ? { bar: 'bg-zinc-900', text: 'text-zinc-900', ring: 'group-hover:ring-zinc-900/20' }
        : { bar: 'bg-rose-600', text: 'text-rose-700', ring: 'group-hover:ring-rose-500/20' },
      hint: profitto >= 0 ? 'Incassi − Spese' : 'Sei in perdita',
    },
  ];

  return (
    <div className="mb-0" data-testid="profit-year-strip">
      <div className="flex items-center justify-between mb-2.5">
        <p className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.22em] text-zinc-500">
          Contabilità · anno {year}
        </p>
        <button
          onClick={() => onNavigate('/contabilita')}
          className="hidden sm:inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-zinc-500 hover:text-zinc-900 transition-colors"
          data-testid="profit-year-strip-view-all"
        >
          Vedi contabilità
          <ArrowRight size={11} weight="bold" />
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {cards.map((c, i) => (
          <button
            key={c.key}
            onClick={() => onNavigate('/contabilita')}
            data-testid={`profit-year-card-${c.key}`}
            className={`group relative bg-white border border-zinc-200 hover:border-zinc-900 text-left overflow-hidden transition-all duration-300 hover:-translate-y-0.5 ring-4 ring-transparent ${c.tone.ring} animate-fadeInUp`}
            style={{ animationDelay: `${i * 70}ms`, animationFillMode: 'both' }}
          >
            <div className={`absolute top-0 left-0 right-0 h-0.5 ${c.tone.bar}`} />
            <div className="relative p-3 sm:p-4">
              <div className="flex items-center gap-1.5 mb-2 sm:mb-3">
                <div className={`w-6 h-6 sm:w-7 sm:h-7 ${c.tone.bar} flex items-center justify-center flex-shrink-0`}>
                  <c.icon size={12} weight="bold" className="text-white" />
                </div>
                <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-[0.18em] text-zinc-500 truncate">
                  {c.label}
                </p>
              </div>
              <p className={`text-xl sm:text-2xl lg:text-3xl font-black ${c.tone.text} tracking-tight leading-none tabular-nums`}>
                {!hasData ? (
                  <span className="text-zinc-300">€ 0</span>
                ) : (
                  <>
                    {c.value < 0 ? '− ' : ''}€&nbsp;{Math.round(Math.abs(c.value)).toLocaleString('it-IT')}
                  </>
                )}
              </p>
              <p className="text-[9px] sm:text-[10px] text-zinc-400 font-medium mt-1.5 sm:mt-2 leading-tight">
                {hasData ? c.hint : 'Aggiungi un bonifico'}
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ==================== ACTION TILES ====================
const ACTION_TILES = [
  {
    label: 'Nuovo contratto',
    hint: 'Firma FES in 2 min',
    icon: FileText,
    to: '/new-contract',
    grad: 'from-zinc-900 to-zinc-700',
    ring: 'group-hover:ring-zinc-900/20',
    dot: 'bg-zinc-900',
    testid: 'action-new-contract',
    primary: true,
  },
  {
    label: 'Nuova ricevuta',
    hint: 'Con marca da bollo',
    icon: Receipt,
    to: '/receipts',
    grad: 'from-teal-600 to-teal-800',
    ring: 'group-hover:ring-teal-500/20',
    dot: 'bg-teal-600',
    testid: 'action-new-receipt',
  },
  {
    label: 'Ricevuta Imposta Soggiorno',
    hint: 'Ospiti × notti × tariffa',
    icon: CoinsIcon,
    to: '/ricevute-imposta-soggiorno',
    grad: 'from-amber-500 to-amber-700',
    ring: 'group-hover:ring-amber-500/20',
    dot: 'bg-amber-500',
    testid: 'action-ricevuta-imposta',
    badge: 'NEW',
  },
  {
    label: 'Aggiorna contabilità',
    hint: 'Airbnb · Booking · Dirette',
    icon: Calculator,
    to: '/contabilita',
    grad: 'from-emerald-600 to-emerald-800',
    ring: 'group-hover:ring-emerald-500/20',
    dot: 'bg-emerald-600',
    testid: 'action-accounting',
  },
  {
    label: 'Imposta di Soggiorno',
    hint: 'Adulti × notti per mese',
    icon: Buildings,
    to: '/imposta-soggiorno',
    grad: 'from-blue-600 to-sky-700',
    ring: 'group-hover:ring-blue-500/20',
    dot: 'bg-blue-600',
    testid: 'action-imposta-soggiorno',
  },
  {
    label: 'Gestione Pulizie',
    hint: 'Coordina staff · realtime',
    icon: SparkleIcon,
    to: '/pulizie',
    grad: 'from-emerald-500 to-teal-700',
    ring: 'group-hover:ring-emerald-500/20',
    dot: 'bg-emerald-600',
    testid: 'action-pulizie',
    badge: 'NEW',
  },
  {
    label: 'QR Code',
    hint: 'Link · WiFi · Dinamico',
    icon: QrCodeIcon,
    to: '/qrcode',
    grad: 'from-violet-600 to-fuchsia-700',
    ring: 'group-hover:ring-violet-500/20',
    dot: 'bg-violet-600',
    testid: 'action-qrcode',
    badge: 'NEW',
  },
  {
    label: 'Eventi intorno a te',
    hint: '419 eventi · 2027 in aggiornamento',
    icon: MapPin,
    to: '/eventi',
    grad: 'from-rose-500 to-pink-700',
    ring: 'group-hover:ring-rose-500/20',
    dot: 'bg-rose-500',
    testid: 'action-events',
  },
];

function ActionTiles({ onNavigate, isAdmin }) {
  const tiles = ACTION_TILES;
  return (
    <div className="mb-6 sm:mb-8" data-testid="action-tiles">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
        {tiles.map((a, i) => (
          <button
            key={a.testid}
            onClick={() => {
              if (a.comingSoon) {
                toast.info(`${a.label}: grandi novità in arrivo. Stiamo lavorando per te.`);
                return;
              }
              onNavigate(a.to);
            }}
            data-testid={a.testid}
            className={`group relative text-left overflow-hidden bg-white border ${a.comingSoon ? 'border-dashed border-zinc-300 hover:border-zinc-500 cursor-default' : 'border-zinc-200 hover:border-zinc-900'} transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg ring-4 ring-transparent ${a.ring} animate-fadeInUp ${a.primary ? 'col-span-2 lg:col-span-1' : ''}`}
            style={{ animationDelay: `${i * 70}ms`, animationFillMode: 'both' }}
          >
            <div className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r ${a.grad}`} />
            <div className={`absolute -right-10 -bottom-10 w-28 h-28 rounded-full bg-gradient-to-br ${a.grad} opacity-0 group-hover:opacity-[0.08] transition-opacity duration-500`} />

            <div className="relative p-4 sm:p-5 flex flex-col gap-3 min-h-[120px] sm:min-h-[140px]">
              {a.badge && (
                <span className="absolute top-2 right-2 inline-flex items-center px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wider bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white animate-pulse-slow">
                  {a.badge}
                </span>
              )}
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 sm:w-9 sm:h-9 bg-gradient-to-br ${a.grad} text-white flex items-center justify-center transition-transform duration-300 group-hover:rotate-90 group-hover:scale-110`}>
                  <Plus size={14} weight="bold" />
                </div>
                <a.icon size={16} weight="bold" className="text-zinc-300 group-hover:text-zinc-900 transition-colors" />
              </div>

              <div className="flex-1">
                <p className="font-black text-xs sm:text-sm text-zinc-900 leading-tight tracking-tight">
                  {a.label}
                </p>
                <p className="text-[10px] sm:text-[11px] text-zinc-400 mt-1 font-medium leading-snug">
                  {a.hint}
                </p>
              </div>

              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-zinc-400 group-hover:text-zinc-900 transition-colors">
                  Apri
                  <ArrowRight size={11} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
                <span className={`w-1.5 h-1.5 rounded-full ${a.dot} opacity-60 group-hover:opacity-100 transition-opacity`} />
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ==================== ACTIVITY BOARD (workspace) ====================
function ActivityBoard({ coach }) {
  const a = coach?.activity || {
    contracts: { total: 0, signed: 0, pending: 0, draft: 0 },
    receipts: 0, bookings: 0, expenses: 0, schedine: 0, event_alerts: 0, imposta_soggiorno: 0,
  };
  const f = coach?.financials || { entrate: 0, uscite: 0, spese: 0 };

  const rows = [
    {
      key: 'contracts',
      icon: FileText,
      label: 'Contratti',
      total: a.contracts.total,
      cells: [
        { label: 'Firmati', value: a.contracts.signed, tone: 'emerald' },
        { label: 'In attesa', value: a.contracts.pending, tone: 'amber' },
        { label: 'Bozze', value: a.contracts.draft, tone: 'zinc' },
      ],
    },
    {
      key: 'receipts',
      icon: Receipt,
      label: 'Ricevute affitto breve',
      total: a.receipts,
      cells: [{ label: 'Emesse totali', value: a.receipts, tone: 'indigo' }],
    },
    {
      key: 'financials',
      icon: CurrencyEur,
      label: 'Movimenti contabili',
      total: a.bookings + a.expenses,
      cells: [
        { label: 'Entrate', value: `€ ${Math.round(f.entrate).toLocaleString('it-IT')}`, tone: 'emerald' },
        { label: 'Uscite', value: `€ ${Math.round(f.uscite).toLocaleString('it-IT')}`, tone: 'amber' },
        { label: 'Spese registrate', value: `€ ${Math.round(f.spese).toLocaleString('it-IT')}`, tone: 'rose' },
      ],
    },
    {
      key: 'imposta_soggiorno',
      icon: Buildings,
      label: 'Imposta di Soggiorno',
      total: a.imposta_soggiorno || 0,
      cells: [{ label: 'Report caricati', value: a.imposta_soggiorno || 0, tone: 'blue' }],
    },
    {
      key: 'events',
      icon: Calendar,
      label: 'Alert eventi',
      total: a.event_alerts,
      cells: [{ label: 'Città monitorate', value: a.event_alerts, tone: 'rose' }],
    },
  ];

  const toneClass = {
    emerald: 'text-emerald-700',
    amber: 'text-amber-700',
    zinc: 'text-zinc-900',
    indigo: 'text-indigo-700',
    rose: 'text-rose-700',
    blue: 'text-blue-700',
  };

  return (
    <div data-testid="activity-board" className="bg-white border border-zinc-200">
      <div className="divide-y divide-zinc-100">
        {rows.map((r, i) => (
          <div
            key={r.key}
            className="flex items-center gap-4 p-4 sm:p-5 hover:bg-zinc-50 transition-colors animate-fadeInUp"
            style={{ animationDelay: `${i * 70}ms`, animationFillMode: 'both' }}
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-9 h-9 bg-zinc-100 flex items-center justify-center flex-shrink-0">
                <r.icon size={16} weight="bold" className="text-zinc-700" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-zinc-900 truncate">{r.label}</p>
                <p className="text-[11px] text-zinc-400">
                  {r.total === 0 ? 'Nessuna attività ancora' : `${r.total} totali`}
                </p>
              </div>
            </div>

            <div className="hidden sm:flex items-center gap-5 flex-shrink-0">
              {r.cells.map((c) => (
                <div key={c.label} className="text-right">
                  <p className={`text-base sm:text-lg font-black leading-none ${toneClass[c.tone]} tabular-nums`}>
                    {c.value ?? 0}
                  </p>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mt-0.5">
                    {c.label}
                  </p>
                </div>
              ))}
            </div>

            <div className="flex sm:hidden items-center gap-3 flex-shrink-0">
              {r.cells.slice(0, 2).map((c) => (
                <div key={c.label} className="text-right">
                  <p className={`text-sm font-black leading-none ${toneClass[c.tone]} tabular-nums`}>
                    {c.value ?? 0}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


export default function DashboardPage() {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [stats, setStats] = useState(null);
  const [accSummary, setAccSummary] = useState(null);
  const [coach, setCoach] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);

  const lang = user?.preferred_language || localStorage.getItem('selectedLanguage') || 'ITA';
  const t = translations[lang] || translations.ITA;

  // Check if we should show the monthly download banner
  const now = new Date();
  const currentDay = now.getDate();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  const showMonthlyBanner = currentDay >= 25;
  const monthNames = ['', 'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
  const signedThisMonth = contracts.filter(c => {
    if (c.status !== 'completed' || c.cleaned) return false;
    const signed = c.signed_at || c.created_at || '';
    return signed.startsWith(`${currentYear}-${String(currentMonth).padStart(2, '0')}`);
  }).length;

  const downloadMonthlyZip = async (year, month) => {
    setDownloadingZip(true);
    try {
      const response = await axios.get(`${API}/contracts/download/monthly-zip?year=${year}&month=${month}`, {
        responseType: 'blob',
        withCredentials: true
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contratti_${monthNames[month]}_${year}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download completato!');
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('Nessun contratto da scaricare per questo mese');
      } else {
        toast.error('Errore durante il download');
      }
    } finally {
      setDownloadingZip(false);
    }
  };

  useEffect(() => {
    checkAuth();
    fetchContracts();
    fetchUnreadCount();
    fetchStats();
    fetchAccSummary();
    fetchCoach();

    // Real-time refresh: poll every 30s + refresh when tab becomes visible
    const pollId = setInterval(() => {
      fetchCoach();
    }, 30000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchCoach();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(pollId);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchContracts = async () => {
    try {
      const response = await axios.get(`${API}/contracts`, { withCredentials: true });
      setContracts(response.data);
    } catch (err) {
      console.error('Error fetching contracts:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`, { withCredentials: true });
      setUnreadCount(response.data.count);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/statistics`, { withCredentials: true });
      setStats(response.data);
    } catch (err) { /* silent */ }
  };

  const fetchAccSummary = async () => {
    try {
      const response = await axios.get(`${API}/accounting/summary`, { withCredentials: true });
      setAccSummary(response.data);
    } catch (err) { /* silent */ }
  };

  const fetchCoach = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/coach`, { withCredentials: true });
      setCoach(response.data);
    } catch (err) { /* silent */ }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const copyShareLink = async (shareToken) => {
    const link = `${window.location.origin}/guest/${shareToken}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success(t.linkCopied);
    } catch (err) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = link;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success(t.linkCopied);
      } catch (e) {
        toast.error('Copia manualmente: ' + link);
      }
      document.body.removeChild(textArea);
    }
  };

  const downloadPdf = async (contractId) => {
    try {
      const response = await axios.get(`${API}/contracts/${contractId}/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contratto_${contractId.slice(-8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF scaricato');
    } catch (err) {
      toast.error('Errore nel download del PDF');
    }
  };

  const deleteContract = async (contractId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo contratto?')) return;
    try {
      await axios.delete(`${API}/contracts/${contractId}`, { withCredentials: true });
      setContracts(prev => prev.filter(c => c.id !== contractId));
      toast.success('Contratto eliminato');
    } catch (err) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) { toast.error('Inserisci la password'); return; }
    setDeletingAccount(true);
    try {
      await axios.post(`${API}/auth/login`, { email: user.email, password: deletePassword });
      await axios.delete(`${API}/auth/delete-account`);
      toast.success('Account eliminato');
      logout();
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Password errata');
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleShare = () => {
    const text = 'Gestisci i tuoi contratti di locazione turistica in modo digitale! Prova gratis: ' + window.location.origin;
    if (navigator.share) {
      navigator.share({ title: 'Locazione Turistica', text, url: window.location.origin });
    } else {
      navigator.clipboard.writeText(text);
      toast.success('Link copiato!');
    }
    setMenuOpen(false);
  };

  return (
    <div className="min-h-full">
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulse-slow { 0%,100%{opacity:1} 50%{opacity:.7} }
        .animate-fadeInUp { animation: fadeInUp .6s ease-out; }
        .animate-pulse-slow { animation: pulse-slow 2.5s ease-in-out infinite; }
      `}} />
      {/* Subscription Banner - Canceling (disdetto, attivo fino a X) */}
      {user?.subscription?.canceling && user?.role !== 'admin' && (
        <div className="bg-amber-50 border-b-2 border-amber-400 px-4 sm:px-6 py-3" data-testid="canceling-banner">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 bg-amber-400 flex items-center justify-center flex-shrink-0 text-amber-900 font-black text-lg">!</div>
              <div>
                <p className="text-sm font-black text-amber-900 tracking-tight">
                  Abbonamento disdetto — resta attivo ancora {user.subscription.days_remaining} {user.subscription.days_remaining === 1 ? 'giorno' : 'giorni'}
                </p>
                <p className="text-xs text-amber-800 mt-0.5">
                  Non verranno effettuati altri addebiti. Ti abbiamo inviato una email di conferma · Ripensaci entro il {user.subscription.subscription_ends_at ? new Date(user.subscription.subscription_ends_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
                </p>
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={() => navigate('/subscription')}
                className="bg-zinc-900 text-white font-bold text-xs uppercase tracking-wider px-4 py-2.5 hover:bg-zinc-800 transition-colors"
                data-testid="canceling-banner-reactivate"
              >
                Riattiva
              </button>
              <button
                onClick={async () => {
                  try {
                    const res = await axios.post(`${API}/subscription/portal`);
                    window.location.href = res.data.portal_url;
                  } catch {
                    navigate('/subscription');
                  }
                }}
                className="bg-white border border-zinc-300 text-zinc-700 font-bold text-xs uppercase tracking-wider px-4 py-2.5 hover:bg-zinc-50 transition-colors"
                data-testid="canceling-banner-portal"
              >
                Vedi su Stripe
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Banner - Trial (7 giorni interni) */}
      {user?.subscription?.plan_type === 'trial' && user?.role !== 'admin' && (
        <div className="bg-red-600 px-4 sm:px-6 py-4 sm:py-5" data-testid="trial-banner">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="text-white">
                <p className="text-lg sm:text-xl font-black tracking-tight">
                  {user.subscription.days_remaining > 0
                    ? `Mancano ${user.subscription.days_remaining} ${user.subscription.days_remaining === 1 ? 'giorno' : 'giorni'} alla fine della prova gratuita`
                    : 'Ultimo giorno di prova gratuita'}
                </p>
                <p className="text-sm sm:text-base text-red-100 mt-1">
                  Abbonati quando vuoi per continuare a usare l'app dopo la prova.
                </p>
              </div>
              <button
                onClick={() => navigate('/subscription')}
                className="bg-white text-red-700 font-bold text-sm uppercase tracking-wider px-6 py-3 hover:bg-red-50 transition-colors text-center flex-shrink-0"
                data-testid="trial-subscribe-btn"
              >
                Scegli un piano
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Banner - Expired */}
      {user?.subscription?.plan_type === 'expired' && user?.role !== 'admin' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/70 backdrop-blur-sm" data-testid="expired-overlay">
          <div className="bg-white mx-4 max-w-lg w-full rounded-2xl shadow-2xl overflow-hidden">
            <div className="bg-teal-700 px-6 py-5 text-center">
              <p className="text-3xl mb-2">&#9749;</p>
              <h2 className="text-xl font-black text-white tracking-tight">La prova gratuita è terminata</h2>
            </div>
            <div className="px-6 py-6">
              <p className="text-sm text-zinc-700 leading-relaxed mb-4">
                Ci piacerebbe offrire il servizio gratuitamente, ma abbiamo fatto il massimo per creare qualcosa di <strong>utile, efficace e low cost</strong>.
              </p>
              <p className="text-sm text-zinc-700 leading-relaxed mb-4">
                Niente pubblicità, niente vendita dei tuoi dati. Il tuo abbonamento non è solo un servizio per te, ma <strong>una spinta per noi a continuare lo sviluppo</strong> e migliorare ogni giorno.
              </p>
              <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-6">
                <p className="text-sm text-red-700">
                  <strong>Attenzione:</strong> se non ti abboni entro <strong>7 giorni</strong>, i tuoi dati verranno cancellati dai nostri server per rispettare la tua privacy.
                </p>
              </div>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/subscription')}
                  className="w-full py-4 bg-teal-700 text-white font-bold text-sm uppercase tracking-wider rounded-xl hover:bg-teal-800 transition-colors"
                  data-testid="expired-subscribe-btn"
                >
                  Scegli un piano &mdash; da &euro;6,99/mese
                </button>
                <button
                  onClick={() => {
                    logout();
                    navigate('/');
                  }}
                  className="w-full py-3 text-sm text-zinc-400 hover:text-zinc-600 transition-colors"
                  data-testid="expired-logout-btn"
                >
                  Non ora, torno dopo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Monthly Download Banner */}
      {showMonthlyBanner && signedThisMonth > 0 && user?.role !== 'admin' && (
        <div className="bg-teal-700 px-4 sm:px-6 py-3 sm:py-4" data-testid="monthly-download-banner">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="text-white">
              <p className="text-sm sm:text-base font-bold">
                Hai {signedThisMonth} contratti firmati a {monthNames[currentMonth]}. Scarica il backup!
              </p>
              <p className="text-xs text-teal-100 mt-0.5">I contratti vengono rimossi dal server il 1° del mese prossimo</p>
            </div>
            <button
              onClick={() => downloadMonthlyZip(currentYear, currentMonth)}
              disabled={downloadingZip}
              className="bg-white text-teal-700 font-bold text-xs sm:text-sm uppercase tracking-wider px-5 py-2.5 hover:bg-teal-50 transition-colors flex items-center gap-2 flex-shrink-0 disabled:opacity-50"
              data-testid="monthly-download-btn"
            >
              <Download size={16} weight="bold" />
              {downloadingZip ? 'Download...' : 'Scarica ZIP'}
            </button>
          </div>
        </div>
      )}

      {/* Header — legacy header disabled: AppLayout now provides the top bar
          and left sidebar (see /components/layout/AppLayout.jsx). */}
      {false && (
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size="lg" />
          </div>

          <div className="flex items-center gap-2 sm:gap-4">
            {/* Admin button */}
            {user?.role === 'admin' && (
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-zinc-900 text-white text-xs sm:text-sm font-bold uppercase tracking-wider hover:bg-zinc-800 transition-colors"
                data-testid="admin-panel-btn"
              >
                <Crown size={16} weight="fill" className="text-amber-400" />
                <span className="hidden sm:inline">Pannello Admin</span>
                <span className="sm:hidden">Admin</span>
              </button>
            )}

            {/* Subscription status */}
            <button
              onClick={() => navigate('/subscription')}
              className="hidden sm:flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-zinc-100 transition-colors"
              data-testid="subscription-button"
            >
              {user?.subscription?.plan_type === 'yearly' && (
                <>
                  <Star size={16} weight="fill" className="text-amber-500" />
                  <span className="text-zinc-700 font-medium">Annuale</span>
                </>
              )}
              {user?.subscription?.plan_type === 'monthly' && (
                <>
                  <CreditCard size={16} className="text-blue-500" />
                  <span className="text-zinc-700 font-medium">Pro</span>
                </>
              )}
              {user?.subscription?.plan_type === 'trial' && (
                <>
                  <Clock size={16} className="text-amber-500" />
                  <span className="text-zinc-700">{user.subscription.days_remaining}gg</span>
                </>
              )}
              {user?.subscription?.plan_type === 'expired' && (
                <>
                  <CreditCard size={16} className="text-red-500" />
                  <span className="text-red-600 font-medium">Abbonati</span>
                </>
              )}
            </button>
            <button 
              className="relative p-2 hover:bg-zinc-100 transition-colors"
              data-testid="notifications-button"
              onClick={() => navigate('/notifications')}
            >
              <Bell size={22} weight="bold" className="text-zinc-700" />
              {unreadCount > 0 && (
                <span className="absolute top-0 right-0 w-5 h-5 bg-green-600 text-white text-xs font-bold flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>
            <div className="hidden sm:block h-8 w-px bg-zinc-200" />
            {/* Profile Menu */}
            <div className="relative">
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="flex items-center gap-1.5 px-2 py-1.5 hover:bg-zinc-100 transition-colors text-sm text-zinc-700"
                data-testid="profile-menu-button"
              >
                <UserCircle size={22} weight="bold" className="text-zinc-600" />
                <span className="hidden sm:inline font-medium">{user?.name}</span>
                <CaretDown size={14} className={`text-zinc-400 transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
              </button>

              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-zinc-200 shadow-lg z-50" data-testid="profile-dropdown">
                    {/* User info */}
                    <div className="px-4 py-3 border-b border-zinc-100">
                      <p className="text-sm font-bold text-zinc-900">{user?.name}</p>
                      <p className="text-xs text-zinc-400 truncate">{user?.email}</p>
                    </div>

                    {/* Menu items */}
                    <div className="py-1">
                      {user?.role === 'admin' && (
                        <button
                          onClick={() => { setMenuOpen(false); navigate('/admin'); }}
                          className="w-full text-left px-4 py-2.5 text-sm text-amber-700 hover:bg-amber-50 flex items-center gap-3 transition-colors font-bold"
                          data-testid="menu-admin"
                        >
                          <Crown size={18} className="text-amber-500" />
                          Pannello Admin
                        </button>
                      )}
                      <button
                        onClick={() => { setMenuOpen(false); navigate('/statistics'); }}
                        className="w-full text-left px-4 py-2.5 text-sm text-zinc-700 hover:bg-zinc-50 flex items-center gap-3 transition-colors"
                        data-testid="menu-statistics"
                      >
                        <ChartBar size={18} className="text-zinc-400" />
                        Statistiche
                      </button>
                      <button
                        onClick={() => { setMenuOpen(false); navigate('/subscription'); }}
                        className="w-full text-left px-4 py-2.5 text-sm text-zinc-700 hover:bg-zinc-50 flex items-center gap-3 transition-colors"
                        data-testid="menu-subscription"
                      >
                        <CreditCard size={18} className="text-zinc-400" />
                        Gestisci abbonamento
                      </button>
                      <button
                        onClick={handleShare}
                        className="w-full text-left px-4 py-2.5 text-sm text-zinc-700 hover:bg-zinc-50 flex items-center gap-3 transition-colors"
                        data-testid="menu-share"
                      >
                        <ShareNetwork size={18} className="text-zinc-400" />
                        Consiglia questa app
                      </button>

                      <div className="border-t border-zinc-100 my-1" />

                      <a
                        href="https://www.vitadahost.it/i-partner-di-vita-da-host.html"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full text-left px-4 py-2.5 text-sm text-zinc-700 hover:bg-zinc-50 flex items-center gap-3 transition-colors"
                        onClick={() => setMenuOpen(false)}
                        data-testid="menu-servizi"
                      >
                        <Briefcase size={18} className="text-zinc-400" />
                        Servizi di Vitadahost.it
                      </a>
                      <a
                        href="https://www.vitadahost.it/app-affitti-brevi-bnb"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full text-left px-4 py-2.5 text-sm text-zinc-700 hover:bg-zinc-50 flex items-center gap-3 transition-colors"
                        onClick={() => setMenuOpen(false)}
                        data-testid="menu-app"
                      >
                        <DeviceMobile size={18} className="text-zinc-400" />
                        Le app di Vitadahost.it
                      </a>
                      <button
                        onClick={() => { setMenuOpen(false); navigate('/contact'); }}
                        className="w-full text-left px-4 py-2.5 text-sm text-zinc-700 hover:bg-zinc-50 flex items-center gap-3 transition-colors"
                        data-testid="menu-contatti"
                      >
                        <EnvelopeSimple size={18} className="text-zinc-400" />
                        Contattaci
                      </button>

                      <div className="border-t border-zinc-100 my-1" />

                      <button
                        onClick={() => { setMenuOpen(false); setShowDeleteConfirm(true); }}
                        className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors"
                        data-testid="menu-delete-account"
                      >
                        <Trash size={18} className="text-red-400" />
                        Cancella account
                      </button>

                      <div className="border-t border-zinc-100 my-1" />

                      <button
                        onClick={() => { setMenuOpen(false); handleLogout(); }}
                        className="w-full text-left px-4 py-2.5 text-sm text-zinc-700 hover:bg-zinc-50 flex items-center gap-3 transition-colors"
                        data-testid="menu-logout"
                      >
                        <SignOut size={18} className="text-zinc-400" />
                        Esci
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>
      )}

      {/* News Ticker - affitti brevi RSS */}
      <NewsTicker />

      {/* One-shot info: contract template updated (May 2026) */}
      <ContractUpdateBanner />

      {/* Push notifications opt-in banner (dismissible) */}
      <PushOptInBanner />

      {/* Delete Account Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="delete-account-modal">
          <div className="bg-white w-full max-w-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <Warning size={24} className="text-red-600" />
              <h2 className="text-lg font-bold text-red-900">Cancella account</h2>
            </div>
            <p className="text-sm text-zinc-600 mb-2">
              Questa azione è <strong>irreversibile</strong>. Verranno eliminati:
            </p>
            <ul className="text-xs text-zinc-500 space-y-1 mb-3 pl-4 list-disc">
              <li>Tutti i contratti, ricevute, schedine alloggiati</li>
              <li>Movimenti contabili e payout (Booking, Airbnb…)</li>
              <li>Pulizie, turni staff, segnalazioni e report</li>
              <li>QR Code dinamici e link generati</li>
              <li>Proprietà, profilo, firma salvata, template personalizzati</li>
              <li>Imposta di soggiorno, alert eventi, notifiche</li>
            </ul>

            <div className="bg-amber-50 border-l-4 border-amber-500 p-3 mb-4">
              <p className="text-xs font-black uppercase tracking-wider text-amber-800 mb-1 inline-flex items-center gap-1.5">
                <Warning size={12} weight="fill" /> Abbonamenti Stripe
              </p>
              <p className="text-[11px] text-amber-900 leading-snug">
                Eventuali abbonamenti <strong>mensili o annuali</strong> attivi verranno
                <strong> cancellati automaticamente</strong> in modo trasparente:
                nessun addebito futuro sulla tua carta.
                Eventuali pagamenti già effettuati non sono rimborsabili.
              </p>
            </div>

            <p className="text-sm text-red-700 font-medium mb-3">Inserisci la password per confermare:</p>
            <input
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              placeholder="La tua password"
              className="w-full border border-red-300 px-3 py-2 text-sm mb-4 focus:outline-none focus:border-red-500"
              data-testid="delete-password-input"
            />
            <div className="flex gap-3">
              <Button
                onClick={handleDeleteAccount}
                disabled={deletingAccount || !deletePassword}
                className="rounded-none bg-red-600 hover:bg-red-700 text-white font-bold text-sm flex-1"
                data-testid="confirm-delete-account-btn"
              >
                {deletingAccount ? 'Eliminazione...' : 'Elimina definitivamente'}
              </Button>
              <Button
                variant="outline"
                onClick={() => { setShowDeleteConfirm(false); setDeletePassword(''); }}
                className="rounded-none border-zinc-300 text-sm"
                data-testid="cancel-delete-btn"
              >
                Annulla
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* HostLand promo banner — dismissibile, riappare dopo 14 giorni */}
        <HostLandPromoBanner />

        {/* Page Header — Modern Hero */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="warm-card p-5 sm:p-7 mb-6 sm:mb-8 relative overflow-hidden"
          data-testid="dashboard-hero"
        >
          <div className="absolute -top-16 -right-10 w-56 h-56 bg-gradient-to-br from-teal-100/60 to-transparent rounded-full blur-3xl pointer-events-none" />
          <div className="relative flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <motion.p
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className="text-xs font-bold uppercase tracking-widest text-teal-700 mb-1"
              >
                Bentornato
              </motion.p>
              <motion.h2
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.15 }}
                className="font-geist text-2xl sm:text-4xl font-black text-zinc-900 tracking-tight"
              >
                Ciao {user?.name?.split(' ')[0] || 'host'}
                <span className="inline-block ml-2" style={{ transform: 'translateY(-2px)' }}>
                  <motion.span
                    animate={{ rotate: [0, 14, -6, 14, -4, 10, 0] }}
                    transition={{ duration: 1.6, delay: 0.3 }}
                    style={{ display: 'inline-block', transformOrigin: '70% 70%' }}
                  >
                    👋
                  </motion.span>
                </span>
              </motion.h2>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.25 }}
                className="text-sm sm:text-base text-zinc-500 mt-1.5 max-w-lg"
              >
                Ecco il colpo d'occhio sulla tua attività oggi &mdash; prenotazioni, contratti, contabilità.
              </motion.p>
            </div>
            <motion.button
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.3, type: 'spring', stiffness: 300 }}
              onClick={() => navigate('/properties')}
              className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white font-semibold text-xs sm:text-sm px-4 sm:px-5 py-2.5 sm:py-3 rounded-full transition-all hover:-translate-y-0.5 shadow-sm hover:shadow-md self-start sm:self-end"
              data-testid="my-properties-btn"
            >
              <House size={15} weight="bold" />
              I miei alloggi
            </motion.button>
          </div>
        </motion.div>

        {/* Onboarding call banner — only yearly plan users with pending call */}
        <OnboardingCallBanner />

        {/* 0. Contabilità anno corrente + trend 6 mesi (grid: 2/3 KPI + 1/3 chart su desktop) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6 sm:mb-8">
          <div className="lg:col-span-2">
            <ProfitYearStrip accSummary={accSummary} onNavigate={navigate} />
          </div>
          <div className="lg:col-span-1">
            <RevenueTrendChart />
          </div>
        </div>

        {/* 1. Adempimenti — Calendario scadenze fiscali (subito sotto la contabilità per massima visibilità) */}
        <DeadlinesCalendar />

        {/* 2. Calendario — Prossimi arrivi/partenze (iCal Airbnb/Booking) */}
        <IcalCalendarSection />

        {/* 3. Scorciatoie — Action Tiles */}
        <ActionTiles onNavigate={navigate} isAdmin={user?.role === 'admin'} />

        {/* 5. Report — Activity Board */}
        <div className="mb-8 sm:mb-10">
          <ActivityBoard coach={coach} />
        </div>


        {/* Contracts */}
        {loading ? (
          <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-500">
            Caricamento...
          </div>
        ) : contracts.length === 0 ? null : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block bg-white border border-zinc-200">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50">
                    <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-zinc-500">Status</th>
                    <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-zinc-500">Lingua</th>
                    <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-zinc-500">Ospite</th>
                    <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-zinc-500">Check-in</th>
                    <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-zinc-500">{t.nights}</th>
                    <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-zinc-500">Importo</th>
                    <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-zinc-500">Creato</th>
                    <th className="text-right py-4 px-6 text-xs font-bold uppercase tracking-wider text-zinc-500">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {contracts.map((contract) => (
                    <tr key={contract.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <span 
                            className={`w-3 h-3 rounded-full ${
                              contract.status === 'completed' 
                                ? 'bg-green-600 status-dot-completed' 
                                : 'bg-zinc-300'
                            }`}
                            data-testid={`contract-status-${contract.id}`}
                          />
                          <span className={`text-sm font-medium ${
                            contract.status === 'completed' ? 'text-green-700' : 'text-zinc-500'
                          }`}>
                            {contract.status === 'completed' ? t.completed : t.pending}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="text-lg">{languageFlags[contract.language]}</span>
                      </td>
                      <td className="py-4 px-6 text-sm text-zinc-900">
                        {contract.guest_data?.full_name || '-'}
                      </td>
                      <td className="py-4 px-6 text-sm text-zinc-700">
                        {contract.rental_data.check_in_date}
                      </td>
                      <td className="py-4 px-6 text-sm text-zinc-700">
                        {contract.rental_data.nights}
                      </td>
                      <td className="py-4 px-6 text-sm font-medium text-zinc-900">
                        &euro;{contract.rental_data.total_rent}
                      </td>
                      <td className="py-4 px-6 text-sm text-zinc-500">
                        {format(new Date(contract.created_at), 'dd/MM/yyyy HH:mm')}
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => navigate(`/contract/${contract.id}`)}
                            className="p-2 hover:bg-zinc-100 transition-colors"
                            title="Visualizza"
                            data-testid={`view-contract-${contract.id}`}
                          >
                            <Eye size={18} className="text-zinc-600" />
                          </button>
                          {contract.status === 'completed' && !contract.cleaned ? (
                            <button
                              onClick={() => downloadPdf(contract.id)}
                              className="flex items-center gap-1 px-2 py-1 bg-teal-700 text-white text-xs font-bold uppercase tracking-wider hover:bg-teal-800 transition-colors"
                              title="Scarica PDF per backup"
                              data-testid={`download-pdf-${contract.id}`}
                            >
                              <Download size={14} weight="bold" />
                              Scarica
                            </button>
                          ) : (
                            <button
                              onClick={() => downloadPdf(contract.id)}
                              className="p-2 hover:bg-zinc-100 transition-colors"
                              title="Scarica PDF"
                              data-testid={`download-pdf-${contract.id}`}
                            >
                              <FilePdf size={18} className="text-zinc-600" />
                            </button>
                          )}
                          <button
                            onClick={() => copyShareLink(contract.share_token)}
                            className="p-2 hover:bg-zinc-100 transition-colors"
                            title={t.share}
                            data-testid={`share-contract-${contract.id}`}
                          >
                            <LinkIcon size={18} className="text-zinc-600" />
                          </button>
                          <button
                            onClick={() => deleteContract(contract.id)}
                            className="p-2 hover:bg-red-50 transition-colors"
                            title={t.delete}
                            data-testid={`delete-contract-${contract.id}`}
                          >
                            <Trash size={18} className="text-red-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
              {contracts.map((contract) => (
                <div 
                  key={contract.id} 
                  className="bg-white border border-zinc-200 p-4"
                  data-testid={`contract-card-${contract.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span 
                        className={`w-3 h-3 rounded-full ${
                          contract.status === 'completed' 
                            ? 'bg-green-600 status-dot-completed' 
                            : 'bg-zinc-300'
                        }`}
                        data-testid={`contract-status-mobile-${contract.id}`}
                      />
                      <span className={`text-sm font-medium ${
                        contract.status === 'completed' ? 'text-green-700' : 'text-zinc-500'
                      }`}>
                        {contract.status === 'completed' ? t.completed : t.pending}
                      </span>
                    </div>
                    <span className="text-lg">{languageFlags[contract.language]}</span>
                  </div>
                  <div className="space-y-1 text-sm mb-3">
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Ospite</span>
                      <span className="font-medium text-zinc-900">{contract.guest_data?.full_name || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Check-in</span>
                      <span className="text-zinc-700">{contract.rental_data.check_in_date}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">{t.nights}</span>
                      <span className="text-zinc-700">{contract.rental_data.nights}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Importo</span>
                      <span className="font-medium text-zinc-900">&euro;{contract.rental_data.total_rent}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-zinc-100">
                    <span className="text-xs text-zinc-400">{format(new Date(contract.created_at), 'dd/MM/yyyy')}</span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => navigate(`/contract/${contract.id}`)}
                        className="p-2 hover:bg-zinc-100 transition-colors"
                        data-testid={`view-contract-mobile-${contract.id}`}
                      >
                        <Eye size={18} className="text-zinc-600" />
                      </button>
                      <button
                        onClick={() => downloadPdf(contract.id)}
                        className="p-2 hover:bg-zinc-100 transition-colors"
                        data-testid={`download-pdf-mobile-${contract.id}`}
                      >
                        <FilePdf size={18} className="text-zinc-600" />
                      </button>
                      <button
                        onClick={() => copyShareLink(contract.share_token)}
                        className="p-2 hover:bg-zinc-100 transition-colors"
                        data-testid={`share-contract-mobile-${contract.id}`}
                      >
                        <LinkIcon size={18} className="text-zinc-600" />
                      </button>
                      <button
                        onClick={() => deleteContract(contract.id)}
                        className="p-2 hover:bg-red-50 transition-colors"
                        data-testid={`delete-contract-mobile-${contract.id}`}
                      >
                        <Trash size={18} className="text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Blog slider — novità dal blog "Vieni da Noi" (RSS custom) */}
        <BlogSlider />
      </main>
    </div>
  );
}
